import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Mail, Phone, Check, AlertCircle, RefreshCw } from 'lucide-react';
import { APP_CONFIG } from '../../utils/constants';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import apiService from '../../services/api';

const Verify: React.FC = () => {
  const { user, sendOTP, verifyOTP, resendOTP, refreshUser } = useAuth();
  const [verificationStep, setVerificationStep] = useState<'email' | 'phone'>('email');
  const [phoneOTP, setPhoneOTP] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    // Auto-redirect if user is already fully verified
    if (user?.verificationStatus === 'verified') {
      navigate('/profiles');
    }
    
    // Start with phone verification if email is already verified
    if (user?.email && verificationStep === 'email') {
      setVerificationStep('phone');
    }
  }, [user, navigate, verificationStep]);

  useEffect(() => {
    // Countdown timer for resend OTP
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendOTP = async () => {
    if (!user?.phone) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const id = await sendOTP(user.phone);
      setVerificationId(id);
      setSuccess('OTP sent to your mobile number');
      setCountdown(60); // 60 second cooldown
    } catch (error: any) {
      setError(error.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!phoneOTP || phoneOTP.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const isValid = await verifyOTP(verificationId, phoneOTP);
      
      if (isValid) {
        setSuccess('Phone number verified successfully!');
        await refreshUser();
        
        // Redirect after successful verification
        setTimeout(() => {
          navigate('/profiles');
        }, 2000);
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      setError(error.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!user?.phone || countdown > 0) return;
    
    setIsResending(true);
    setError('');
    
    try {
      const id = await resendOTP(user.phone);
      setVerificationId(id);
      setSuccess('OTP resent successfully');
      setCountdown(60);
    } catch (error: any) {
      setError(error.message || 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyEmail = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // In a real app, this would send verification email
      // For now, we'll simulate email verification
      await apiService.verifyEmail('dummy-token');
      setSuccess('Email verified successfully!');
      setVerificationStep('phone');
    } catch (error: any) {
      setError(error.message || 'Email verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">RPS</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Verify Your Account</h2>
          <p className="mt-2 text-gray-600">
            Please verify your email and phone number to continue
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          {/* Verification Steps */}
          <div className="flex items-center space-x-4 mb-6">
            <div className={`flex items-center space-x-2 ${
              user.email ? 'text-green-600' : 'text-primary-600'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                user.email ? 'bg-green-100' : 'bg-primary-100'
              }`}>
                {user.email ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
              </div>
              <span className="text-sm font-medium">Email</span>
            </div>
            
            <div className="flex-1 h-px bg-gray-200"></div>
            
            <div className={`flex items-center space-x-2 ${
              user.isVerified ? 'text-green-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                user.isVerified ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {user.isVerified ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Phone className="w-4 h-4" />
                )}
              </div>
              <span className="text-sm font-medium">Phone</span>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-600">{success}</span>
            </div>
          )}

          {/* Email Verification */}
          {verificationStep === 'email' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Verify Email Address
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  We'll send a verification link to <strong>{user.email}</strong>
                </p>
                <button
                  onClick={handleVerifyEmail}
                  disabled={isLoading}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" text="" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      <span>Send Verification Email</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Phone Verification */}
          {verificationStep === 'phone' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Verify Phone Number
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter the 6-digit OTP sent to <strong>{user.phone}</strong>
                </p>

                {!verificationId ? (
                  <button
                    onClick={handleSendOTP}
                    disabled={isLoading}
                    className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <LoadingSpinner size="sm" text="" />
                    ) : (
                      <>
                        <Phone className="w-4 h-4" />
                        <span>Send OTP</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={phoneOTP}
                      onChange={(e) => setPhoneOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-lg tracking-wider"
                      maxLength={6}
                    />
                    
                    <button
                      onClick={handleVerifyOTP}
                      disabled={isLoading || phoneOTP.length !== 6}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isLoading ? (
                        <LoadingSpinner size="sm" text="" />
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Verify OTP</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleResendOTP}
                      disabled={isResending || countdown > 0}
                      className="w-full text-gray-600 py-2 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isResending ? (
                        <LoadingSpinner size="sm" text="" />
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          <span>
                            {countdown > 0 ? `Resend OTP (${countdown}s)` : 'Resend OTP'}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="text-center text-sm text-gray-600">
          <p>
            Need help? Contact us at{' '}
            <a href={`mailto:${APP_CONFIG.supportEmail}`} className="text-primary-600 hover:text-primary-500">
              {APP_CONFIG.supportEmail}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Verify;